import React, { useState, useRef, useEffect } from 'react';
import { ChatProps } from '../../../types/expertise-detail.types';
import { useChat } from '../../../hooks/useChat';
import { formatRelativeTime } from '../../../utils/formatters';
import { Loading } from '@/ui/components/atoms/Loading';

export const Chat: React.FC<ChatProps> = ({ affaireId }) => {
  const [messageInput, setMessageInput] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  
  const { thread, messages, loading, error, sendMessage, refreshMessages } = useChat(affaireId);

  // Auto-scroll to bottom when new messages arrive
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Get current user info from localStorage
  const getCurrentUser = () => {
    const user = localStorage.getItem('user');
    if (user) {
      try {
        return JSON.parse(user);
      } catch {
        return null;
      }
    }
    return null;
  };

  const currentUser = getCurrentUser();

  const handleSendMessage = async () => {
    if (!messageInput.trim()) return;

    const messageText = messageInput.trim();
    setMessageInput('');

    try {
      await sendMessage(messageText);
      scrollToBottom();
    } catch (err) {
      // Error is handled in the hook
      setMessageInput(messageText); // Restore message on error
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const isOwnMessage = (role: string) => {
    if (!currentUser) return false;
    return currentUser.role === role;
  };

  if (loading && !thread) {
    return (
      <div className="chat-container loading">
        <Loading size="medium" text="Chargement de la conversation..." />
      </div>
    );
  }

  if (error && !thread) {
    return (
      <div className="chat-container error">
        <div className="chat-error">
          <p>Erreur lors du chargement de la conversation</p>
          <p className="error-detail">{error}</p>
          <button onClick={refreshMessages} className="retry-button">
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="chat-container">
      <div className="chat-header">
        <h3>Messages</h3>
        <button onClick={refreshMessages} className="refresh-button" title="Rafraîchir">
          🔄
        </button>
      </div>

      <div className="chat-messages">
        {messages.length === 0 ? (
          <div className="no-messages">
            <p>Aucun message pour le moment</p>
            <p className="hint">Commencez une conversation en envoyant un message</p>
          </div>
        ) : (
          <>
            {messages.map((message) => (
              <div
                key={message.id}
                className={`message ${isOwnMessage(message.role) ? 'own' : 'other'} ${message.role}`}
              >
                <div className="message-header">
                  <span className="message-sender">
                    {message.first_name} {message.last_name}
                  </span>
                  <span className="message-role">{message.role}</span>
                </div>
                <div className="message-content">
                  {message.message_text}
                </div>
                <div className="message-time">
                  {formatRelativeTime(message.created_at)}
                </div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      <div className="chat-input-section">
        <textarea
          ref={inputRef}
          value={messageInput}
          onChange={(e) => setMessageInput(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder="Tapez votre message..."
          className="chat-input"
          rows={3}
        />
        <div className="chat-actions">
          <span className="hint-text">Appuyez sur Entrée pour envoyer</span>
          <button
            onClick={handleSendMessage}
            disabled={!messageInput.trim() || loading}
            className="send-button"
          >
            Envoyer
          </button>
        </div>
      </div>

      <style>{`
        .chat-container {
          display: flex;
          flex-direction: column;
          height: 600px;
          background: white;
          border-radius: 12px;
          border: 1px solid #E5E7EB;
          overflow: hidden;
          box-shadow: 0 4px 24px rgba(0, 0, 0, 0.06);
        }

        .chat-container.loading,
        .chat-container.error {
          justify-content: center;
          align-items: center;
        }

        .chat-error {
          text-align: center;
          padding: 40px;
        }

        .chat-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 16px 24px;
          background: #F8F9FA;
          border-bottom: 1px solid #E5E7EB;
        }

        .chat-header h3 {
          margin: 0;
          font-size: 18px;
          font-weight: 600;
          color: #002035;
          font-family: 'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
        }

        .refresh-button {
          padding: 6px 12px;
          background: white;
          border: 1px solid #E5E7EB;
          border-radius: 8px;
          cursor: pointer;
          font-size: 16px;
          transition: all 0.2s ease;
        }

        .refresh-button:hover {
          background: #F8F9FA;
          border-color: #ef5343;
          color: #ef5343;
          transform: rotate(180deg);
        }

        .chat-messages {
          flex: 1;
          overflow-y: auto;
          padding: 24px;
          background: #F8F9FA;
        }

        .no-messages {
          text-align: center;
          padding: 60px 20px;
          color: #6B7280;
          font-family: 'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
        }

        .hint {
          font-size: 14px;
          color: #9CA3AF;
          margin-top: 8px;
        }

        .message {
          margin-bottom: 20px;
          max-width: 70%;
          animation: slideIn 0.3s ease;
        }

        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .message.own {
          margin-left: auto;
        }

        .message.other {
          margin-right: auto;
        }

        .message-header {
          display: flex;
          gap: 8px;
          align-items: center;
          margin-bottom: 4px;
        }

        .message.own .message-header {
          justify-content: flex-end;
        }

        .message-sender {
          font-size: 13px;
          font-weight: 600;
          color: #002035;
          font-family: 'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
        }

        .message-role {
          font-size: 11px;
          padding: 2px 6px;
          background: #E5E7EB;
          border-radius: 12px;
          color: #6B7280;
          text-transform: uppercase;
          font-family: 'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
        }

        .message.admin .message-role {
          background: #DBEAFE;
          color: #1E40AF;
        }

        .message.expert .message-role {
          background: #E9D5FF;
          color: #7C3AED;
        }

        .message.client .message-role {
          background: #D1FAE5;
          color: #065F46;
        }

        .message-content {
          padding: 12px 16px;
          border-radius: 12px;
          font-size: 14px;
          line-height: 1.5;
          word-wrap: break-word;
        }

        .message.own .message-content {
          background: #ef5343;
          color: white;
          border-bottom-right-radius: 4px;
        }

        .message.other .message-content {
          background: white;
          color: #002035;
          border: 1px solid #E5E7EB;
          border-bottom-left-radius: 4px;
        }

        .message-time {
          font-size: 11px;
          color: #9CA3AF;
          margin-top: 4px;
        }

        .message.own .message-time {
          text-align: right;
        }

        .chat-input-section {
          padding: 16px 24px;
          background: white;
          border-top: 1px solid #E5E7EB;
        }

        .chat-input {
          width: 100%;
          padding: 12px;
          border: 1px solid #E5E7EB;
          border-radius: 8px;
          font-size: 14px;
          resize: none;
          font-family: 'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
          transition: border-color 0.2s ease;
          color: #002035;
        }

        .chat-input:focus {
          outline: none;
          border-color: #ef5343;
          box-shadow: 0 0 0 3px rgba(239, 83, 67, 0.1);
        }

        .chat-actions {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-top: 12px;
        }

        .hint-text {
          font-size: 12px;
          color: #9CA3AF;
          font-family: 'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
        }

        .send-button {
          padding: 8px 24px;
          background: #ef5343;
          color: white;
          border: none;
          border-radius: 50px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;
          font-family: 'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
        }

        .send-button:hover:not(:disabled) {
          background: #e63b2a;
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(239, 83, 67, 0.3);
        }

        .send-button:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .retry-button {
          padding: 8px 20px;
          background: #ef5343;
          color: white;
          border: none;
          border-radius: 50px;
          font-weight: 500;
          cursor: pointer;
          margin-top: 16px;
          font-family: 'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
          transition: all 0.2s ease;
        }
        
        .retry-button:hover {
          background: #e63b2a;
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(239, 83, 67, 0.3);
        }

        .error-detail {
          font-size: 13px;
          color: #ef5343;
          margin-top: 8px;
          font-family: 'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
        }

        /* Custom scrollbar */
        .chat-messages::-webkit-scrollbar {
          width: 8px;
        }

        .chat-messages::-webkit-scrollbar-track {
          background: #F8F9FA;
          border-radius: 4px;
        }

        .chat-messages::-webkit-scrollbar-thumb {
          background: #CBD5E1;
          border-radius: 4px;
        }

        .chat-messages::-webkit-scrollbar-thumb:hover {
          background: #ef5343;
        }

        @media (max-width: 768px) {
          .chat-container {
            height: 500px;
          }

          .message {
            max-width: 85%;
          }

          .chat-header {
            padding: 12px 16px;
          }

          .chat-messages {
            padding: 16px;
          }

          .chat-input-section {
            padding: 12px 16px;
          }
        }
      `}</style>
    </div>
  );
};